<template>
  <div
    class="background"
    :class="theme"
  >
    <v-app-bar
      color="transparent"
      flat
    >
      <v-toolbar-title>
        <img
          :src="logo"
          alt="logo"
          height="28"
        >
      </v-toolbar-title>
      <v-spacer />
      <v-toolbar-items>
        <v-btn
          v-if="user && user.email"
          :to="{name: 'listScans'}"
          class="px-2 py-1"
          color="primary"
          depressed
          small
        >
          Go to my account
        </v-btn>
        <div
          v-else
          class="sign-up"
        >
          <span class="pa-3">Already a member?</span>
          <LoginButton />
        </div>
      </v-toolbar-items>
    </v-app-bar>
    <v-container>
      <!-- fill-height -->
      <v-row>
        <v-col cols="4">
          <v-card
            color="transparent"
            flat
          >
            <v-card-title
              :class="{'white--text': theme === 'openvoxel'}"
              class="display-2"
              style="word-break: break-word;"
            >
              <template v-if="theme === 'openvoxel'">
                Secure repository for your imaging data.
              </template>
              <template v-else>
                Get full insight into your body.
              </template>
            </v-card-title>
            <v-card-text
              :class="{'white--text': theme === 'openvoxel'}"
            >
              <template v-if="theme === 'openvoxel'">
                openVOXEL stores and visualizes researchers’ anatomical
                and functional data from magnetic resonance imaging.
              </template>
              <template v-else>
                neuralike stores and makes sense of your medical images,
                so you can focus on the things that matter.
              </template>
            </v-card-text>
            <v-card-actions
              class="ma-1"
            >
              <v-btn
                :to="{name: 'brain', params: {view_type: '3D'}}"
                color="primary"
                class="text-uppercase"
              >
                View a sample image
              </v-btn>
              <v-btn
                v-if="theme !== 'openvoxel'"
                class="text-uppercase"
                @click="videoShown = true"
              >
                <v-icon>mdi-play</v-icon>
                See how it works
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row
        class="mt-5 fill-height"
      >
        <v-col cols="4">
          <v-card
            class="features ma-3 pa-3"
            height="100%"
          >
            <v-card-title class="headline text-uppercase">
              <template v-if="theme === 'openvoxel'">
                Store your data
              </template>
              <template v-else>
                Got images?
              </template>
            </v-card-title>
            <v-card-text>
              <template v-if="theme === 'openvoxel'">
                Upload your anatomical and functional images using our
                Amazon-secured cloud. We will deposit your files for
                secure storage and make them visible for you to analyze.
              </template>
              <template v-else>
                Just upload your images you have received from your medical
                image provider. Use our Amazon-secured cloud to upload,
                store, and interpret your medical images.
              </template>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card
            class="features ma-3 pa-3"
            height="100%"
          >
            <v-card-title class="headline text-uppercase">
              <template v-if="theme === 'openvoxel'">
                Learn more
              </template>
              <template v-else>
                Learn more
              </template>
            </v-card-title>
            <v-card-text>
              <template v-if="theme === 'openvoxel'">
                Create your free account today to upload, store, and view
                anatomical and functional neuroimages, without the need of
                downloading any software.
              </template>
              <template v-else>
                See the benefits of neuralike.
              </template>
            </v-card-text>
            <v-card-actions v-if="theme !== 'openvoxel'">
              <v-btn
                text
                @click="planOptionsShown = true"
              >
                Plan options
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card
            v-if="!(user && user.email)"
            class="sample-report ma-3 pa-3"
            color="primary"
            dark
            height="100%"
            @click="bottomDrawerShown = true"
          >
            <v-card-title>
              <div class="headline text-uppercase">
                Create an account
              </div>
              <v-icon class="arrow-icon">
                mdi-arrow-right
              </v-icon>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-row>
        <v-col
          class="text-right"
          align-self="end"
        >
          <v-btn
            @click="$emit('showBottomDrawer')"
          >
            Privacy, Terms, Company &amp; More
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog
      v-model="videoShown"
      width="672"
    >
      <VideoCard @close="videoShown = false" />
    </v-dialog>
    <v-dialog
      v-model="planOptionsShown"
      width="1024"
    >
      <PlanOptions
        @close="planOptionsShown = false"
        @createFreeAccount="createFreeAccount"
        @createPaidAccount="createPaidAccount"
      />
    </v-dialog>
    <v-bottom-sheet v-model="bottomDrawerShown">
      <RegisterPanel
        :plan="selectedPlan"
      />
    </v-bottom-sheet>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import NeuralikeLogo from '@/assets/Neuralike-Logo-Horizontal.svg';
import OpenvoxelLogo from '@/assets/Openvoxel-Logo.svg';
import LoginButton from '@/components/Home/LoginButton.vue';
import PlanOptions from '@/components/Home/PlanOptions.vue';
import RegisterPanel from '@/components/Home/RegisterPanel.vue';
import VideoCard from '@/components/Home/VideoCard.vue';

const theme = process.env.VUE_APP_THEME;

export default {
  name: 'HomePage',
  components: {
    LoginButton,
    PlanOptions,
    RegisterPanel,
    VideoCard,
  },
  data() {
    return {
      bottomDrawerShown: false,
      videoShown: false,
      planOptionsShown: false,
      theme,
      logo: theme === 'openvoxel' ? OpenvoxelLogo : NeuralikeLogo,
      selectedPlan: 'free',
    };
  },
  computed: {
    ...mapState('auth', ['user']),
  },
  methods: {
    createPaidAccount(plan) {
      this.planOptionsShown = false;
      this.selectedPlan = plan;
      this.bottomDrawerShown = true;
    },
    createFreeAccount() {
      this.selectedPan = 'free';
      this.planOptionsShown = false;
      if (this.user && this.user.email) {
        this.$router.push({ name: 'listScans' });
      } else {
        this.bottomDrawerShown = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.background {
  background-size: cover;
  height: 100%;
  &.neuralike {
    background-image: url('~@/assets/Neuralike-homepage-background.jpg');
  }
  &.openvoxel {
    background-image: url('~@/assets/Openvoxel-homepage-background.jpg');
  }
}

.sign-up {
  display: flex;
  align-items: center;
}

.features {
  border-top: 4px solid var(--v-secondary-base);
}

.sample-report {
  background: linear-gradient(45deg, var(--v-primary-base) 0%, var(--v-secondary-base) 100%);

  .arrow-icon {
    bottom: 20px;
    position: absolute;
    right: 20px;
  }
}
</style>
